import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LockKey, ArrowRight, WarningCircle } from "@phosphor-icons/react";
import { AuthShell } from "./ForgotPassword";
import { PasswordInput } from "../components/PasswordInput";
import { validatePasswordPair, PASSWORD_RULE_TEXT } from "../lib/passwordPolicy";

/**
 * Shared credential-setup screen for both the invite (set first password) and the
 * password-reset (set new password) flows. The Supabase action link redirects here with the
 * session tokens in the URL hash; supabase-js (detectSessionInUrl) establishes the session.
 * We wait briefly for that session; if none arrives, the link is expired / already used / invalid.
 */
export default function SetPasswordScreen({ mode = "invite" }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | ready | invalid
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const isReset = mode === "reset";
  const title = isReset ? "Set a new password" : "Set your password";

  useEffect(() => {
    let done = false;
    const settle = (s) => { if (!done) { done = true; setStatus(s); } };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) settle("ready");
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session) settle("ready");
    });
    const timer = setTimeout(() => settle("invalid"), 6500);

    return () => { done = true; clearTimeout(timer); sub?.subscription?.unsubscribe?.(); };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    const perr = validatePasswordPair(pw, pw2);
    if (perr) { toast.error(perr); return; }
    setBusy(true);
    try {
      // Server-coordinated completion: password mutation + audit + full session revocation are
      // reliably coupled on the backend (the current setup/recovery session token is attached by api).
      await api.post(isReset ? "/auth/complete-reset" : "/auth/complete-setup", { password: pw });
      try { await supabase.auth.signOut(); } catch (_) { /* session already revoked server-side */ }
      toast.success(isReset ? "Password updated — please sign in" : "Password set — please sign in");
      navigate("/staff/login", { replace: true });
    } catch (err) {
      toast.error(err?.displayMessage || "We couldn't set your password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (status === "checking") {
    return <AuthShell title={title}><div className="mono text-sm py-6" data-testid="setpw-checking">Verifying your link…</div></AuthShell>;
  }

  if (status === "invalid") {
    return (
      <AuthShell title={title}>
        <div className="space-y-4" data-testid="setpw-invalid">
          <div className="flex items-start gap-2 text-sm">
            <WarningCircle size={20} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
            <span>This link is invalid, has expired, or has already been used. Links are valid for 24 hours and can be used once.</span>
          </div>
          {isReset ? (
            <a href="/forgot-password" className="btn-primary w-full justify-center" data-testid="setpw-request-new">
              Request a new reset link
            </a>
          ) : (
            <p className="text-xs text-[hsl(var(--muted))]">
              Please ask your Air12 admin to resend your invite.
            </p>
          )}
          <a href="/staff/login" className="text-xs underline text-[hsl(var(--muted))] block text-center" data-testid="setpw-back-login">
            Back to sign in
          </a>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={title}>
      <form onSubmit={onSubmit} className="space-y-3" data-testid="setpw-form">
        <label className="block">
          <div className="overline mb-1.5">NEW PASSWORD</div>
          <PasswordInput value={pw} required minLength={8}
                 autoComplete="new-password" onChange={(e) => setPw(e.target.value)} data-testid="setpw-password-input" />
        </label>
        <label className="block">
          <div className="overline mb-1.5">CONFIRM PASSWORD</div>
          <PasswordInput value={pw2} required minLength={8}
                 autoComplete="new-password" onChange={(e) => setPw2(e.target.value)} data-testid="setpw-confirm-input" />
        </label>
        <p className="text-[10px] text-[hsl(var(--muted))]">{PASSWORD_RULE_TEXT}</p>
        <button type="submit" className="btn-primary w-full justify-center mt-2" disabled={busy || !pw || !pw2}
                data-testid="setpw-submit-button">
          <LockKey size={14} weight="bold" /> {isReset ? "Update password" : "Set password & continue"}
          <ArrowRight size={14} weight="bold" />
        </button>
      </form>
    </AuthShell>
  );
}
